'use client'

import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import type { Components } from 'react-markdown'
import { cn } from '@/lib/utils'
import { getApiUrl } from '@/lib/api-client'
import { Icons } from '@/components/ui/icons'
import { Avatar } from '@/components/ui/avatar'
import type { ChatMessage as ChatMessageType } from '@/context/AIContext'
import { EntityChip } from './EntityChip'
import { NavigationCard } from './NavigationCard'
import { ActionChipRow } from './ActionChipRow'
import { RedirectionBlock } from './RedirectionBlock'
import { DidYouMeanCard, type DidYouMeanOption } from './DidYouMeanCard'
import { getFreshSession } from '@/lib/fresh-session'

interface ChatMessageProps {
  message: ChatMessageType
  userName?: string
  userImage?: string | null
  /** Continue the conversation when a "Did you mean?" option is picked. */
  onSuggestionSelect?: (message: string) => void
  /** True while a response is streaming — suppresses double-sends. */
  isBusy?: boolean
}

// ---------------------------------------------------------------------------
// findDidYouMean — pull the first ranked suggestion set out of the tool calls.
//
// Any lookup tool may attach `did_you_mean` when an identifier missed. The
// first one wins: a single turn asking about one record produces one miss,
// and stacking multiple cards would be noise.
// ---------------------------------------------------------------------------

interface DidYouMeanPayload {
  query: string
  entityType?: string
  options: DidYouMeanOption[]
}

function findDidYouMean(
  toolCalls: ChatMessageType['toolCalls'],
): DidYouMeanPayload | null {
  if (!toolCalls) return null

  for (const call of toolCalls) {
    const result = call.result as Record<string, unknown> | undefined
    if (!result) continue

    const options = result.did_you_mean
    if (!Array.isArray(options) || options.length === 0) continue

    // `query_echo` is set by the search tools; the export tool uses
    // `requested_sample_name`. Without either there is nothing to title
    // the card with, so skip rather than render a card saying "undefined".
    const query =
      (result.query_echo as string | undefined) ??
      (result.requested_sample_name as string | undefined)
    if (!query) continue

    return {
      query,
      entityType: result.did_you_mean_entity as string | undefined,
      options: options as DidYouMeanOption[],
    }
  }
  return null
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

// ---------------------------------------------------------------------------
// extractInternalLinks — scan raw markdown for internal hrefs
// Returns a deduped list of { href, label } in order of appearance.
// Uses the same prefix list as the <a> renderer override below.
// ---------------------------------------------------------------------------

const INTERNAL_PREFIXES = [
  '/claims',
  '/samples',
  '/rfis',
  '/disagreements',
  '/universes',
  '/workbench',
  '/reports',
  '/settings',
  '/ai/',
]

interface InternalLink {
  href: string
  label: string
}

function extractInternalLinks(markdown: string): InternalLink[] {
  // Match [label](href) where href is an internal path
  const MD_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g
  const seen = new Set<string>()
  const links: InternalLink[] = []

  let match: RegExpExecArray | null
  while ((match = MD_LINK_RE.exec(markdown)) !== null) {
    const label = match[1].trim()
    const href  = match[2].trim()

    const isInternal =
      href === '/'
      || INTERNAL_PREFIXES.some(prefix => href.startsWith(prefix))

    if (isInternal && !seen.has(href)) {
      seen.add(href)
      links.push({ href, label })
    }
  }

  return links
}

// ---------------------------------------------------------------------------
// Markdown component overrides — every element mapped to brand design tokens
// ---------------------------------------------------------------------------

const markdownComponents: Components = {
  // Headings
  h1: ({ children }) => (
    <h1 className="text-base font-bold text-foreground mt-4 first:mt-0 mb-1">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold text-foreground mt-3 first:mt-0 mb-1">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold text-foreground mt-3 first:mt-0 mb-1">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm font-semibold text-muted-foreground mt-2 first:mt-0">
      {children}
    </h4>
  ),

  // Paragraph
  p: ({ children }) => (
    <p className="text-sm leading-relaxed text-foreground">{children}</p>
  ),

  // Strong / Em / Code (inline)
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-muted-foreground">{children}</em>
  ),
  code: ({ children, className }) => {
    // Block code (inside <pre>) gets no extra wrapper styling here — handled by <pre>
    const isInline = !className
    if (isInline) {
      return (
        <code className="px-1.5 py-0.5 rounded bg-brand-navy/10 text-brand-navy font-mono text-xs border border-brand-navy/10">
          {children}
        </code>
      )
    }
    return (
      <code className={cn('font-mono text-xs text-foreground', className)}>
        {children}
      </code>
    )
  },

  // Code block wrapper
  pre: ({ children }) => (
    <pre className="mt-2 mb-2 rounded-lg bg-brand-navy/5 border border-brand-navy/10 p-3 overflow-x-auto text-xs font-mono text-foreground whitespace-pre-wrap">
      {children}
    </pre>
  ),

  // Unordered list
  ul: ({ children }) => (
    <ul className="space-y-1 pl-1 my-1">{children}</ul>
  ),

  // Ordered list
  ol: ({ children }) => (
    <ol className="space-y-1 pl-1 my-1 list-none">{children}</ol>
  ),

  // List item — handles both ul and ol via the `ordered` and `index` props
  li: ({ children, ...props }) => {
    // react-markdown passes `ordered` as a prop to li nodes
    const ordered = (props as unknown as { ordered?: boolean }).ordered
    const index = (props as unknown as { index?: number }).index
    return (
      <li className="flex gap-2 items-start text-sm leading-relaxed">
        {ordered ? (
          <span className="text-brand-cornflower font-semibold min-w-[1.25rem] pt-px shrink-0">
            {typeof index === 'number' ? index + 1 : ''}. 
          </span>
        ) : (
          <span className="w-1.5 h-1.5 rounded-full bg-brand-cornflower/70 mt-2 shrink-0" />
        )}
        <div className="flex-1 min-w-0">{children}</div>
      </li>
    )
  },

  // Blockquote
  blockquote: ({ children }) => (
    <blockquote className="pl-3 border-l-2 border-brand-cornflower/50 text-muted-foreground italic my-2">
      {children}
    </blockquote>
  ),

  // Horizontal rule
  hr: () => (
    <hr className="my-3 border-border/40" />
  ),

  // Links — internal paths become EntityChips; external links open safely
  a: ({ href, children }) => {
    if (!href) return <span>{children}</span>

    // Detect internal frontend paths (set by AI via _ui_link)
    const isInternal =
      href === '/'
      || INTERNAL_PREFIXES.some(prefix => href.startsWith(prefix))

    if (isInternal) {
      // Render as an EntityChip — opens in new tab, preserves conversation
      const label = typeof children === 'string'
        ? children
        : (children as React.ReactNode[])?.[0]?.toString?.() ?? href
      return (
        <EntityChip
          label={label}
          href={href}
          className="mx-0.5 align-middle"
        />
      )
    }

    // External link — standard anchor with safe attributes
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-cornflower underline-offset-2 hover:underline transition-colors"
      >
        {children}
      </a>
    )
  },

  // -------------------------------------------------------------------------
  // TABLE — the main feature: renders structured data beautifully
  // -------------------------------------------------------------------------
  table: ({ children }) => (
    <div className="my-3 overflow-x-auto rounded-lg border border-brand-cornflower/20 shadow-sm">
      <table className="w-full text-sm border-collapse">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-gradient-to-r from-brand-cornflower/10 to-brand-purple/10 border-b border-brand-cornflower/20">
      {children}
    </thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-border/30">{children}</tbody>
  ),
  tr: ({ children }) => (
    <tr className="transition-colors hover:bg-brand-cornflower/5 even:bg-muted/20">
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold text-brand-navy uppercase tracking-wide whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-foreground text-sm">
      {children}
    </td>
  ),
}

// ---------------------------------------------------------------------------
// MarkdownContent — thin wrapper around ReactMarkdown
// ---------------------------------------------------------------------------

function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="space-y-2 text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

// ---------------------------------------------------------------------------
// BriefingDownloadCard — renders when AI calls generate_executive_briefing
// PDF is generated on-demand when the user opens the URL in a new tab.
//
// Supports both preset periods (period=last_7_days) AND custom date ranges
// (start_date=2026-06-10&end_date=2026-06-15). The LLM parses natural
// language dates and provides ISO strings.
//
// View strategy: open the backend URL DIRECTLY in a new tab, passing the
// access token as a query parameter. The backend validates the token and
// streams the PDF with Content-Disposition: inline.
//
// WHY DIRECT BACKEND URL: On PROD, GKE ingress routes all /api/* requests
// directly to the backend, bypassing Next.js API routes. The previous
// approach used a Next.js proxy at /api/briefings, which worked locally
// (no ingress) but returned 404 on PROD because the backend has no such
// route — the actual endpoint is /api/reports/briefings/generate.
//
// WHY TOKEN IN QUERY: window.open() is a raw browser navigation — it
// cannot set Authorization headers. The backend's security layer accepts
// the token from a query param as a fallback, validated via Keycloak.
// ---------------------------------------------------------------------------

/** Format an ISO date string (YYYY-MM-DD) into a readable label. */
function formatIsoDate(iso: string): string {
  try {
    const [year, month, day] = iso.split('-').map(Number)
    const d = new Date(year, month - 1, day)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  } catch {
    return iso
  }
}

function BriefingDownloadCard({
  period,
  startDate,
  endDate,
}: {
  period?: string
  startDate?: string
  endDate?: string
}) {
  const [opened, setOpened] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const periodLabels: Record<string, string> = {
    last_7_days: 'Last 7 Days',
    last_30_days: 'Last 30 Days',
    last_90_days: 'Last 90 Days',
    this_month: 'This Month',
    this_quarter: 'This Quarter',
    ytd: 'Year to Date',
  }

  // Build dynamic label from whatever params are available
  const label = startDate && endDate
    ? `${formatIsoDate(startDate)} – ${formatIsoDate(endDate)}`
    : startDate
    ? `Since ${formatIsoDate(startDate)}`
    : periodLabels[period || ''] || period || 'Last 30 Days'

  const handleView = async () => {
    // Get the user's access token from the session
    const session = await getFreshSession()
    const accessToken = (session as unknown as { accessToken?: string })?.accessToken
    if (!accessToken) {
      // Session expired — user needs to re-login
      return
    }

    // Build query params for the briefing period
    const params = new URLSearchParams()
    if (period && period !== 'custom') params.set('period', period)
    if (startDate) params.set('start_date', startDate)
    if (endDate) params.set('end_date', endDate)
    if (!period && !startDate) params.set('period', 'last_30_days')

    // Pass the access token as a query param — window.open can't set headers
    params.set('token', accessToken)

    // Open the backend endpoint DIRECTLY — bypasses the Next.js proxy.
    // getApiUrl() returns the NEXT_PUBLIC_API_URL with HTTPS upgrade.
    const apiUrl = getApiUrl()
    window.open(`${apiUrl}/api/reports/briefings/generate?${params.toString()}`, '_blank')
    setOpened(true)
    setLoading(true)
    // Brief spinner to signal PDF is being generated in the new tab (~3-5s)
    setTimeout(() => setLoading(false), 4000)
  }

  return (
    <button
      onClick={handleView}
      id="briefing-view-card"
      className={cn(
        'flex items-center gap-3 px-4 py-3 mt-2 rounded-xl w-full text-left',
        'bg-gradient-to-r from-brand-cornflower/10 via-brand-purple/5 to-transparent',
        'border border-brand-cornflower/25',
        'hover:from-brand-cornflower/15 hover:via-brand-purple/10',
        'hover:border-brand-cornflower/40',
        'transition-all duration-200 cursor-pointer',
        'group',
        opened && 'border-emerald-300/40',
      )}
    >
      <div className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg',
        'bg-gradient-to-br from-brand-navy to-brand-purple',
        'shadow-sm group-hover:shadow-md transition-shadow'
      )}>
        {loading ? (
          // Tiny inline spinner while the new tab generates the PDF
          <svg
            className="h-4 w-4 text-white animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
        ) : opened ? (
          <Icons.checkCircle className="h-4 w-4 text-white" strokeWidth={1.5} />
        ) : (
          <Icons.fileText className="h-4 w-4 text-white" strokeWidth={1.5} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground">
          Executive Briefing — {label}
        </div>
        <div className={cn(
          'text-xs',
          opened ? 'text-emerald-600' : 'text-muted-foreground'
        )}>
          {opened
            ? 'Opened in new tab — download from the PDF viewer'
            : 'Click to view PDF report'}
        </div>
      </div>
      <Icons.externalLink
        className="h-4 w-4 text-brand-cornflower/60 group-hover:text-brand-cornflower transition-colors shrink-0"
        strokeWidth={1.5}
      />
    </button>
  )
}

// ---------------------------------------------------------------------------
// ReportDownloadCard — renders when AI calls generate_report
// Triggers a browser download of the Excel report (.xlsx).
//
// WHY DOWNLOAD instead of new tab: report exports return Content-Disposition:
// attachment (not inline), so a new tab would auto-close. We trigger a real
// browser download by creating a hidden <a> element with the download attribute.
//
// Auth: same pattern as BriefingDownloadCard — token passed as query param.
// ---------------------------------------------------------------------------

function buildMonthName(monthNum: number): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return months[(monthNum - 1) % 12] ?? String(monthNum)
}

function ReportDownloadCard({
  reportId,
  reportName,
  filters,
}: {
  reportId: string
  reportName: string
  filters?: Record<string, string | number>
}) {
  const [downloaded, setDownloaded] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  // Build a human-readable period label from filters
  const periodLabel = React.useMemo(() => {
    if (!filters) return ''
    const parts: string[] = []
    if (filters.month && filters.year) {
      parts.push(`${buildMonthName(Number(filters.month))} ${filters.year}`)
    } else if (filters.year) {
      parts.push(String(filters.year))
    } else if (filters.workload_month) {
      parts.push(String(filters.workload_month))
    }
    if (filters.program) parts.push(String(filters.program))
    if (filters.jurisdiction) parts.push(String(filters.jurisdiction))
    return parts.length > 0 ? `\u2014 ${parts.join(', ')}` : ''
  }, [filters])

  const handleDownload = async () => {
    const session = await getFreshSession()
    const accessToken = (session as unknown as { accessToken?: string })?.accessToken
    if (!accessToken) return

    const params = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') {
          params.set(k, String(v))
        }
      })
    }
    params.set('token', accessToken)

    const apiUrl = getApiUrl()
    const url = `${apiUrl}/api/reports/${reportId}/export?${params.toString()}`

    // Trigger a real browser download via hidden anchor
    const a = document.createElement('a')
    a.href = url
    a.download = `${reportId}_Export.xlsx`
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)

    setDownloaded(true)
    setLoading(true)
    setTimeout(() => setLoading(false), 3000)
  }

  return (
    <button
      onClick={handleDownload}
      id={`report-download-card-${reportId}`}
      className={cn(
        'flex items-center gap-3 px-4 py-3 mt-2 rounded-xl w-full text-left',
        'bg-gradient-to-r from-brand-cornflower/10 via-brand-purple/5 to-transparent',
        'border border-brand-cornflower/25',
        'hover:from-brand-cornflower/15 hover:via-brand-purple/10',
        'hover:border-brand-cornflower/40',
        'transition-all duration-200 cursor-pointer',
        'group',
        downloaded && 'border-emerald-300/40',
      )}
    >
      <div className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg',
        'bg-gradient-to-br from-brand-navy to-brand-purple',
        'shadow-sm group-hover:shadow-md transition-shadow'
      )}>
        {loading ? (
          <svg
            className="h-4 w-4 text-white animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : downloaded ? (
          <Icons.checkCircle className="h-4 w-4 text-white" strokeWidth={1.5} />
        ) : (
          <Icons.download className="h-4 w-4 text-white" strokeWidth={1.5} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground">
          {reportName} ({reportId}){periodLabel}
        </div>
        <div className={cn(
          'text-xs',
          downloaded ? 'text-emerald-600' : 'text-muted-foreground'
        )}>
          {downloaded
            ? 'Download started — check your downloads folder'
            : 'Click to download Excel report'}
        </div>
      </div>
      <Icons.download
        className="h-4 w-4 text-brand-cornflower/60 group-hover:text-brand-cornflower transition-colors shrink-0"
        strokeWidth={1.5}
      />
    </button>
  )
}

// ---------------------------------------------------------------------------
// TrackingLogDownloadCard — renders when AI calls export_tracking_log
// Triggers a browser download of the UTL or RSL Excel file.
// Same download mechanism as ReportDownloadCard.
// ---------------------------------------------------------------------------

function TrackingLogDownloadCard({
  logType,
  sampleId,
  sampleName,
}: {
  logType: string
  sampleId?: string
  sampleName?: string
}) {
  const [downloaded, setDownloaded] = React.useState(false)
  const [loading, setLoading] = React.useState(false)

  const isUtl = logType === 'utl'
  const displayName = isUtl
    ? 'Universe Tracking Log (UTL)'
    : `Review Sample Log (RSL)${sampleName ? ` — ${sampleName}` : ''}`

  const handleDownload = async () => {
    const session = await getFreshSession()
    const accessToken = (session as unknown as { accessToken?: string })?.accessToken
    if (!accessToken) return

    const params = new URLSearchParams()
    params.set('token', accessToken)

    const apiUrl = getApiUrl()
    const url = isUtl
      ? `${apiUrl}/api/reports/utl-export?${params.toString()}`
      : `${apiUrl}/api/reports/rsl-export/${sampleId}?${params.toString()}`

    const filename = isUtl ? 'UTL_Export.xlsx' : `RSL_${sampleName ?? sampleId ?? 'export'}.xlsx`

    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)

    setDownloaded(true)
    setLoading(true)
    setTimeout(() => setLoading(false), 3000)
  }

  return (
    <button
      onClick={handleDownload}
      id={`tracking-log-card-${logType}`}
      className={cn(
        'flex items-center gap-3 px-4 py-3 mt-2 rounded-xl w-full text-left',
        'bg-gradient-to-r from-brand-cornflower/10 via-brand-purple/5 to-transparent',
        'border border-brand-cornflower/25',
        'hover:from-brand-cornflower/15 hover:via-brand-purple/10',
        'hover:border-brand-cornflower/40',
        'transition-all duration-200 cursor-pointer',
        'group',
        downloaded && 'border-emerald-300/40',
      )}
    >
      <div className={cn(
        'flex items-center justify-center w-9 h-9 rounded-lg',
        'bg-gradient-to-br from-brand-navy to-brand-purple',
        'shadow-sm group-hover:shadow-md transition-shadow'
      )}>
        {loading ? (
          <svg
            className="h-4 w-4 text-white animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : downloaded ? (
          <Icons.checkCircle className="h-4 w-4 text-white" strokeWidth={1.5} />
        ) : (
          <Icons.fileText className="h-4 w-4 text-white" strokeWidth={1.5} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground">
          {displayName}
        </div>
        <div className={cn(
          'text-xs',
          downloaded ? 'text-emerald-600' : 'text-muted-foreground'
        )}>
          {downloaded
            ? 'Download started — check your downloads folder'
            : 'Click to download Excel export'}
        </div>
      </div>
      <Icons.download
        className="h-4 w-4 text-brand-cornflower/60 group-hover:text-brand-cornflower transition-colors shrink-0"
        strokeWidth={1.5}
      />
    </button>
  )
}

// ---------------------------------------------------------------------------
// ChatMessage
// ---------------------------------------------------------------------------

export function ChatMessage({
  message,
  userName,
  userImage,
  onSuggestionSelect,
  isBusy = false,
}: ChatMessageProps) {
  const isUser = message.role === 'user'
  const isLoading = message.isLoading
  const didYouMean = isUser ? null : findDidYouMean(message.toolCalls)

  return (
    <div
      className={cn(
        'flex gap-3',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        {isUser ? (
          <Avatar
            src={userImage}
            fallback={userName || 'U'}
            size="sm"
          />
        ) : (
          <div className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full',
            'bg-gradient-to-br from-brand-navy to-brand-purple',
            'shadow-soft'
          )}>
            <Icons.sparkles className="h-4 w-4 text-white" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        'flex max-w-[85%] flex-col gap-1',
        isUser ? 'items-end' : 'items-start'
      )}>
        <div
          className={cn(
            'rounded-2xl px-4 py-3',
            'transition-all duration-200',
            isUser
              ? 'bg-brand-navy text-white rounded-br-md'
              : 'bg-white/90 text-foreground border border-border/50 rounded-bl-md shadow-soft',
            isLoading && 'animate-pulse'
          )}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 py-1">
              <div className="flex gap-1">
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '0ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '150ms' }} />
                <span className="h-2 w-2 animate-bounce rounded-full bg-brand-muted/60" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm text-brand-muted">Thinking...</span>
            </div>
          ) : isUser ? (
            <p className="text-sm whitespace-pre-wrap leading-relaxed">
              {message.content}
            </p>
          ) : (
            <MarkdownContent content={message.content} />
          )}
        </div>

        {/* Timestamp */}
        <span className={cn(
          'text-[10px] text-muted-foreground/60',
          isUser ? 'pr-1' : 'pl-1'
        )}>
          {formatTime(message.timestamp)}
        </span>

        {/* Grounded data indicator */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full',
            'bg-gradient-to-r from-emerald-50 to-teal-50',
            'border border-emerald-200/50',
            'text-[10px] text-emerald-700',
          )}>
            <Icons.checkCircle className="h-3 w-3" strokeWidth={1.5} />
            <span className="font-medium">Grounded in live data</span>
            <span className="text-emerald-500 font-normal">
              · {message.toolCalls.map(tc => tc.name.replace(/_/g, ' ')).join(', ')}
            </span>
          </div>
        )}

        {/* Executive Briefing card — only if tool succeeded with a period_key */}
        {message.toolCalls?.some(tc => tc.name === 'generate_executive_briefing') && (() => {
          const briefingCall = message.toolCalls!.find(tc => tc.name === 'generate_executive_briefing')
          const result = briefingCall?.result as Record<string, unknown> | undefined
          // Don't render if tool returned an error (e.g. access_denied for non-admin)
          if (result?.error) return null

          const period = result?.period_key as string | undefined
          const startDate = result?._briefing_start_date as string | undefined
          const endDate = result?._briefing_end_date as string | undefined

          // Must have either period_key or start_date to construct a URL
          if (!period && !startDate) return null

          return (
            <BriefingDownloadCard
              period={period}
              startDate={startDate}
              endDate={endDate}
            />
          )
        })()}

        {/* Report download card — only if generate_report tool succeeded */}
        {message.toolCalls?.some(tc => tc.name === 'generate_report') && (() => {
          const reportCall = message.toolCalls!.find(tc => tc.name === 'generate_report')
          const result = reportCall?.result as Record<string, unknown> | undefined
          // Don't render if tool returned an error or missing download marker
          if (result?.error || !result?._report_download) return null
          return (
            <ReportDownloadCard
              reportId={result.report_id as string}
              reportName={result.report_name as string}
              filters={result._report_filters as Record<string, string | number> | undefined}
            />
          )
        })()}

        {/* Tracking log download card — only if export_tracking_log tool succeeded */}
        {message.toolCalls?.some(tc => tc.name === 'export_tracking_log') && (() => {
          const logCall = message.toolCalls!.find(tc => tc.name === 'export_tracking_log')
          const result = logCall?.result as Record<string, unknown> | undefined
          if (result?.error || !result?._tracking_log_download) return null
          return (
            <TrackingLogDownloadCard
              logType={result.log_type as string}
              sampleId={result.sample_id as string | undefined}
              sampleName={result.sample_name as string | undefined}
            />
          )
        })()}

        {/* "Did you mean?" — a lookup missed, but ranked near-misses exist.
            Rendered only when a send handler is wired up; without one the
            options would not be selectable and the markdown list the AI
            already wrote covers the same ground. */}
        {didYouMean && onSuggestionSelect && (
          <DidYouMeanCard
            query={didYouMean.query}
            entityType={didYouMean.entityType}
            options={didYouMean.options}
            onSelect={onSuggestionSelect}
            disabled={isBusy}
          />
        )}

        {/* Redirection block — only the PRIMARY (first) internal link.
            Secondary/contextual links already appear as inline EntityChip
            pills in the message body, so rendering them again as cards is
            redundant and confusing (e.g. asking about a claim should NOT
            also show a Sample card just because the AI mentioned the sample).
            SUPPRESSED for report/tracking-log tools: their summary tables contain
            many incidental entity deep-links that are not primary nav targets. */}
        {!isUser && (() => {
          // Don't show a nav card when the response is a report download —
          // the report data rows contain _ui_link fields for every entity in
          // the report, and we don't want to surface any of those as navigation.
          const hasReportTool = message.toolCalls?.some(
            tc => tc.name === 'generate_report' || tc.name === 'export_tracking_log'
          )
          if (hasReportTool) return null

          // Suppressed for "Did you mean?" responses: the first internal link
          // is a *suggestion*, not the record the user asked for, and
          // promoting it to a primary nav card would reassert the very
          // confusion this flow exists to remove. The card carries its own
          // per-option open-in-new-tab affordance instead.
          if (didYouMean) return null

          const links = extractInternalLinks(message.content)
          if (links.length === 0) return null

          // Exclude any link that duplicates the navIntent destination
          const navPath = message.navIntent?.path
          const filtered = links.filter(link => !navPath || link.href !== navPath)

          // Only show the first (primary) link as a card
          const primary = filtered[0]
          if (!primary) return null

          return <RedirectionBlock key={primary.href} href={primary.href} label={primary.label} />
        })()}

        {/* Navigation card — rendered when AI called navigate_to_entity */}
        {message.navIntent && (
          <NavigationCard navIntent={message.navIntent} />
        )}

        {/* Action chip row — rendered when API returns action_chips */}
        {message.actionChips && message.actionChips.length > 0 && (
          <ActionChipRow chips={message.actionChips} />
        )}
      </div>
    </div>
  )
}
